import api from '../lib/api';
import { buildListQuery, type ListQueryInput } from '../lib/buildListQuery';
import type { CommissionEntry, CommissionEntryInput, Paginated } from '../types';

export type ListCommissionEntriesParams = ListQueryInput;

export interface CommissionEntryFilterOptions {
  years: number[];
  regions: string[];
  zip_codes: string[];
}

export interface CommissionEntryTotals {
  sum_commissions: string;
  sum_deductions: string;
  sum_bonus: string;
  net_commissions: string;
  count_commissions: number;
  count_deductions: number;
  count_bonus: number;
  count_total: number;
}

export const commissionEntryService = {
  list: async (params?: ListCommissionEntriesParams): Promise<Paginated<CommissionEntry>> => {
    const response = await api.get<Paginated<CommissionEntry>>('/commission_entries', { params: buildListQuery(params) });
    return response.data;
  },

  filterOptions: async (): Promise<CommissionEntryFilterOptions> => {
    const response = await api.get<CommissionEntryFilterOptions>('/commission_entries/filter_options');
    return response.data;
  },

  totals: async (params?: ListCommissionEntriesParams): Promise<CommissionEntryTotals> => {
    // Reuses the same q-param shape as list() so totals reflect the active filters.
    // Page/per_page/sort are dropped server-side; only q matters.
    const response = await api.get<CommissionEntryTotals>('/commission_entries/totals', { params: buildListQuery(params) });
    return response.data;
  },

  get: async (id: number): Promise<CommissionEntry> => {
    const response = await api.get<{ data: { attributes: CommissionEntry } }>(`/commission_entries/${id}`);
    return response.data.data.attributes;
  },

  create: async (data: CommissionEntryInput): Promise<CommissionEntry> => {
    const response = await api.post<{ data: { attributes: CommissionEntry } }>('/commission_entries', {
      commission_entry: data,
    });
    return response.data.data.attributes;
  },

  update: async (id: number, data: Partial<CommissionEntryInput>): Promise<CommissionEntry> => {
    const response = await api.patch<{ data: { attributes: CommissionEntry } }>(`/commission_entries/${id}`, {
      commission_entry: data,
    });
    return response.data.data.attributes;
  },

  destroy: async (id: number): Promise<void> => {
    await api.delete(`/commission_entries/${id}`);
  },
};

export default commissionEntryService;
